/**
 * Синхронизация с сервером: тянем снимок, отправляем очередь.
 *
 * Правило простое: читаем всегда из локального хранилища, пишем всегда в
 * очередь. Сеть — фоновый процесс, который эти две области сводит. Так экраны
 * не знают, есть связь или нет, и не должны это учитывать.
 */

import { api, apiBase, getToken } from '../api'
import type { Access, Client, Contact, NamedRef, Source, Task } from '../types'
import {
  dropFromOutbox,
  enqueue,
  getIdMap,
  getSnapshotPart,
  listOutbox,
  markOutboxFailure,
  outboxSize,
  putSnapshotPart,
  rememberServerId,
  setSyncedAt,
  type OutboxOp,
} from './db'
import { protect, reveal, unlocked } from './vault'

/** Сколько раз пробуем отправить операцию, прежде чем показать её технику как
 *  застрявшую. Дальше решает человек: повторить или отменить. */
const MAX_ATTEMPTS = 5

export interface SyncState {
  online: boolean
  syncing: boolean
  pending: number
  /** Время сервера на момент последнего снимка. Только серверное: часы
   *  телефона могут врать, а «данные от 3 сентября» должно значить именно это. */
  syncedAt: string | null
  /** Счётчик изменений локальных данных — и своих, и пришедших с сервера.
   *  Экраны перечитывают себя по нему, не заглядывая, что именно поменялось. */
  rev: number
  lastError: string | null
}

type Listener = (s: SyncState) => void

const listeners = new Set<Listener>()
let state: SyncState = {
  online: navigator.onLine,
  syncing: false,
  pending: 0,
  syncedAt: null,
  rev: 0,
  lastError: null,
}

function emit(patch: Partial<SyncState>) {
  state = { ...state, ...patch }
  listeners.forEach((fn) => fn(state))
}

export function onSyncState(fn: Listener): () => void {
  listeners.add(fn)
  fn(state)
  return () => listeners.delete(fn)
}

export function getSyncState(): SyncState {
  return state
}

/** Идентификатор операции. Он же защищает от дублей при переотправке. */
export function newUid(): string {
  return crypto.randomUUID()
}

// ---------- снимок ----------

export async function pullSnapshot(withSecrets = true): Promise<void> {
  if (!getToken()) return
  emit({ syncing: true, lastError: null })
  try {
    const url = `${apiBase()}/api/sync/snapshot?with_secrets=${withSecrets}`
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
    if (!res.ok) throw new Error(`сервер ответил ${res.status}`)
    const snap = await res.json()

    await Promise.all([
      putSnapshotPart('tasks', snap.tasks ?? []),
      putSnapshotPart('clients', snap.clients ?? []),
      putSnapshotPart('contacts', snap.contacts ?? []),
      // Доступы — единственное, что шифруется: там пароли к системам клиентов.
      // Остальное неприятно потерять, но это не ключи от чужого хозяйства.
      putSnapshotPart('accesses', [(await protect(snap.accesses ?? [])) as unknown]),
      putSnapshotPart('sources', snap.sources ?? []),
      putSnapshotPart('products', snap.products ?? []),
      putSnapshotPart('portal_services', snap.portal_services ?? []),
    ])
    await setSyncedAt(snap.server_time)
    emit({ syncedAt: snap.server_time, rev: state.rev + 1 })
  } catch (e: any) {
    emit({ lastError: e?.message ?? 'не удалось получить данные' })
  } finally {
    emit({ syncing: false })
  }
}

// ---------- чтение: всегда из локального ----------

/**
 * Задачи = снимок сервера + черновики, созданные без связи.
 *
 * Черновик живёт, пока его операция не ушла: как только сервер подтвердил
 * создание, задача приходит в обычном снимке, и дубль надо убрать. Признак —
 * исчезновение операции из очереди.
 */
async function readTasks(): Promise<Task[]> {
  const [rows, drafts] = await Promise.all([
    getSnapshotPart<Task>('tasks'),
    getSnapshotPart<Task & { client_uid?: string }>('draft_tasks'),
  ])
  if (drafts.length === 0) return rows

  const queued = new Set((await listOutbox()).map((o) => o.clientUid))
  const alive = drafts.filter((d) => d.client_uid && queued.has(d.client_uid))
  if (alive.length !== drafts.length) await putSnapshotPart('draft_tasks', alive)
  return [...alive, ...rows]
}

export const local = {
  tasks: readTasks,
  clients: () => getSnapshotPart<Client>('clients'),
  contacts: () => getSnapshotPart<Contact>('contacts'),
  accesses: async (): Promise<Access[]> => {
    const stored = await getSnapshotPart<unknown>('accesses')
    return reveal<Access[]>(stored[0], [])
  },
  /** Введён ли код: пока нет, доступы показать нечем — так и пишем на экране. */
  accessesLocked: () => !unlocked(),
  sources: () => getSnapshotPart<Source>('sources'),
  products: () => getSnapshotPart<NamedRef>('products'),
  portalServices: () => getSnapshotPart<NamedRef>('portal_services'),
}

// ---------- запись: всегда в очередь ----------

export async function queueOp(
  kind: OutboxOp['kind'],
  payload: Record<string, unknown>,
): Promise<{ clientUid: string; localId: number | null }> {
  const clientUid = newUid()
  await applyLocally(kind, payload, clientUid)
  await enqueue({ clientUid, kind, payload, createdAt: new Date().toISOString() })
  emit({ pending: await outboxSize(), rev: state.rev + 1 })
  void flush()
  return { clientUid, localId: (payload.localId as number) ?? null }
}

/**
 * Сразу показываем результат действия, не дожидаясь сервера.
 *
 * Без этого нажатие «Старт» в подвале без связи выглядит как сломанная кнопка:
 * техник жмёт, ничего не меняется, жмёт ещё раз. Правки те же, что сделает
 * сервер, и следующая синхронизация их просто перезапишет своей версией.
 */
async function applyLocally(
  kind: OutboxOp['kind'],
  payload: Record<string, unknown>,
  clientUid: string,
): Promise<void> {
  const p = payload as any

  if (kind === 'task.create') {
    const drafts = await getSnapshotPart<Task & { client_uid?: string }>('draft_tasks')
    // Отрицательный номер: настоящий придёт с сервера, а перепутать нельзя.
    const id = Math.min(0, ...drafts.map((d) => d.id)) - 1
    payload.localId = id
    await putSnapshotPart('draft_tasks', [
      { ...draftTask(id, p), client_uid: clientUid } as Task & { client_uid: string },
      ...drafts,
    ])
    return
  }

  /** Остановка задачи так, как это сделает сервер: время дописывается в учтённое. */
  const stopped = (t: Task, status: Task['work_status']): Task => {
    const from = t.running_since ? new Date(t.running_since).getTime() : 0
    const grown = from ? Math.max(0, Math.floor((Date.now() - from) / 1000)) : 0
    return { ...t, running_since: null, tracked_seconds: t.tracked_seconds + grown, work_status: status }
  }

  const patch = (t: Task): Task => {
    switch (kind) {
      case 'timer.start':
        return { ...t, running_since: new Date().toISOString(), work_status: 'in_progress' }
      case 'timer.stop':
        return stopped(t, p.finish ? 'done' : 'paused')
      case 'task.status':
        return { ...t, work_status: p.status }
      case 'task.update':
        return { ...t, ...(p.body as Partial<Task>) }
      default:
        return t
    }
  }

  const id = Number(p.taskId)
  for (const part of ['tasks', 'draft_tasks'] as const) {
    const rows = await getSnapshotPart<Task>(part)
    // Таймер один на человека: запуская новую задачу, сервер останавливает
    // предыдущую. Повторяем это и локально, иначе на экране «идут» сразу две.
    const next = rows.map((t) => {
      if (t.id === id) return patch(t)
      if (kind === 'timer.start' && t.running_since) return stopped(t, 'paused')
      return t
    })
    if (next.some((t, i) => t !== rows[i])) await putSnapshotPart(part, next)
  }
}

/** Заготовка задачи, созданной без связи: поля те же, значения — пустые. */
function draftTask(id: number, p: any): Task {
  const now = new Date().toISOString()
  return {
    id,
    title: p.title ?? '',
    description: p.description ?? null,
    client_id: p.client_id,
    subclient_id: p.subclient_id ?? null,
    project_id: p.project_id ?? null,
    source_id: p.source_id ?? null,
    // Задачу без связи заводят сами: из заявки её завести нельзя — для этого
    // нужен сервер.
    ticket_id: null,
    ticket_kind: null,
    ticket_title: null,
    ticket_subject: null,
    ticket_category_title: null,
    estimate: p.estimate ?? null,
    assignee_id: p.assignee_id ?? 0,
    work_status: p.work_status ?? 'new',
    payment_status: 'to_pay',
    priority: p.priority ?? 'normal',
    is_support: p.is_support ?? false,
    work_date: p.work_date ?? now.slice(0, 10),
    due_date: p.due_date ?? null,
    rate_override: null,
    fixed_amount: null,
    board_order: 0,
    created_at: now,
    updated_at: now,
    completed_at: null,
    client_name: p.client_name ?? null,
    subclient_name: null,
    project_name: null,
    source_name: p.source_name ?? null,
    assignee_name: p.assignee_name ?? null,
    tracked_seconds: 0,
    running_since: null,
    first_started_at: null,
    last_stopped_at: null,
    amount: 0,
    rate: 0,
    rate_missing: false,
    entries: [],
    estimate_lines: [],
    estimate_changes: [],
  }
}

/**
 * Отправляет одну операцию. Бросает — значит не отправилась.
 *
 * Отрицательный номер задачи — временный, выданный на телефоне. Настоящий
 * появляется, когда уходит создание, и подставляется здесь.
 */
async function send(op: OutboxOp): Promise<void> {
  const p = { ...(op.payload as any) }

  if (typeof p.taskId === 'number' && p.taskId < 0) {
    const real = (await getIdMap())[String(p.taskId)]
    if (real === undefined) {
      // Создание ещё не ушло. Порядок очереди это гарантирует, так что сюда
      // мы попадаем только если создание навсегда отвалилось: не отправляем.
      const e: any = new Error('задача ещё не создана на сервере')
      e.status = 409
      throw e
    }
    p.taskId = real
  }

  switch (op.kind) {
    case 'timer.start':
      await api.startTimer(p.taskId, op.clientUid)
      return
    case 'timer.stop':
      await api.stopTimer(p.taskId, p.finish)
      return
    case 'task.create': {
      const body = { ...p }
      // Служебные поля черновика на сервер не нужны.
      delete body.client_name
      delete body.source_name
      delete body.assignee_name
      delete body.localId
      const created = await api.createTask({ ...body, client_uid: op.clientUid })
      if (typeof p.localId === 'number' && created && typeof created.id === 'number') {
        await rememberServerId(p.localId, created.id)
      }
      return
    }
    case 'task.update':
      await api.updateTask(p.taskId, p.body)
      return
    case 'task.status':
      await api.changeStatus(p.taskId, p.status)
      return
    case 'task.estimate':
      await api.setEstimate(p.taskId, p.lines, p.comment)
      return
  }
}

let flushing = false

/**
 * Отправляет очередь по порядку. Останавливается на первой сетевой ошибке —
 * порядок важен: «создать задачу» должно уйти раньше «запустить по ней таймер».
 *
 * Ошибку самого сервера (400-е) пропускаем дальше, пометив операцию: она не
 * станет успешной от повторов, а очередь из-за неё встанет навсегда.
 */
export async function flush(): Promise<void> {
  if (flushing || !navigator.onLine || !getToken()) return
  flushing = true
  emit({ syncing: true })
  let sent = 0
  try {
    for (const op of await listOutbox()) {
      if (op.attempts >= MAX_ATTEMPTS) continue
      try {
        await send(op)
        await dropFromOutbox(op.id!)
        sent += 1
      } catch (e: any) {
        const status = e?.status ?? 0
        const clientFault = status >= 400 && status < 500
        await markOutboxFailure(op, e?.message ?? 'ошибка отправки')
        if (!clientFault) break // сеть или сервер — ждём лучшего момента
      }
    }
    emit({ pending: await outboxSize() })
  } finally {
    flushing = false
    emit({ syncing: false })
  }
  // Черновик держится, пока его операция в очереди. Как только она ушла,
  // задачу надо получить настоящей — иначе она на секунду пропадёт с экрана.
  if (sent > 0) await pullSnapshot()
}

/** Запускается один раз при старте приложения. */
export function startSync(): () => void {
  const goOnline = () => {
    emit({ online: true })
    void flush().then(() => pullSnapshot())
  }
  const goOffline = () => emit({ online: false })

  window.addEventListener('online', goOnline)
  window.addEventListener('offline', goOffline)

  void outboxSize().then((n) => emit({ pending: n }))
  // Состояние связи читаем заново: между загрузкой модуля и входом в программу
  // техник успевает спуститься в подвал.
  emit({ online: navigator.onLine })
  if (navigator.onLine) goOnline()

  // Периодически подтягиваем снимок: связь могла не пропадать, но данные
  // на сервере могли поменяться — например, администратор завёл задачу.
  const timer = window.setInterval(() => {
    if (navigator.onLine) void pullSnapshot()
  }, 5 * 60 * 1000)

  return () => {
    window.removeEventListener('online', goOnline)
    window.removeEventListener('offline', goOffline)
    window.clearInterval(timer)
  }
}
